import { Link } from "react-router-dom";
import { Crown, Facebook, Instagram, Youtube, Send, ShieldCheck } from "lucide-react";
import { useSettings } from "@/hooks/useSettings";
import { resolveImage } from "@/lib/api";

export function Footer() {
  const s = useSettings();
  const socials = [
    { url: s.social_facebook, Icon: Facebook, label: "facebook" },
    { url: s.social_instagram, Icon: Instagram, label: "instagram" },
    { url: s.social_youtube, Icon: Youtube, label: "youtube" },
    { url: s.social_telegram, Icon: Send, label: "telegram" },
  ].filter((x) => x.url);

  return (
    <footer className="relative mt-20 border-t border-gold/15 bg-gradient-to-b from-emerald-dark/40 to-obsidian" data-testid="site-footer">
      <div className="h-0.5 w-full bg-gradient-to-r from-gold/60 via-emerald/70 to-gold/60" />
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-12">
        <div className="grid gap-8 md:grid-cols-3">
          <div>
            <div className="flex items-center gap-2.5">
              {s.logo_url ? (
                <img src={resolveImage(s.logo_url)} alt="logo" className="h-9 w-9 rounded-lg object-cover" />
              ) : (
                <span className="flex h-9 w-9 items-center justify-center rounded-lg gold-gradient">
                  <Crown className="h-5 w-5 text-obsidian" />
                </span>
              )}
              <span className="font-display text-2xl gold-text">{s.site_name || "Golden Live"}</span>
            </div>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-slate-400">
              {s.footer_text || "Golden Live — Shine brighter, go live. Recharge safely through official resellers only."}
            </p>
          </div>

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">Quick Links</h4>
            <ul className="mt-4 space-y-2 text-sm text-slate-300">
              <li><Link to="/notices" className="hover:text-gold">Notice Board</Link></li>
              <li><Link to="/top-agencies" className="hover:text-gold">Top Agencies</Link></li>
              <li><Link to="/resellers" className="hover:text-gold">Resellers</Link></li>
              <li><Link to="/recharge" className="hover:text-gold">Recharge Guide</Link></li>
              <li><Link to="/official-host" className="hover:text-gold">Become a Host</Link></li>
              <li><Link to="/host-salary" className="hover:text-gold">Host Salary Lookup</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">Contact</h4>
            <p className="mt-4 text-sm text-slate-300">{s.contact_info || "support@goldenlive.com"}</p>
            <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-gold/10 px-3 py-1.5 text-xs font-semibold text-gold">
              <ShieldCheck className="h-4 w-4" /> Recharge only via official resellers
            </div>
            {socials.length > 0 && (
              <div className="mt-4 flex gap-3">
                {socials.map(({ url, Icon, label }) => (
                  <a key={label} href={url} target="_blank" rel="noreferrer"
                     className="flex h-10 w-10 items-center justify-center rounded-full border border-gold/25 text-slate-300 hover:text-gold hover:border-gold/60">
                    <Icon className="h-5 w-5" />
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-10 flex flex-col items-center justify-between gap-3 border-t border-gold/10 pt-6 text-xs text-slate-500 sm:flex-row">
          <p>© {new Date().getFullYear()} {s.site_name || "Golden Live"}. All rights reserved.</p>
          <Link to="/admin" className="hover:text-gold" data-testid="footer-admin-link">Admin</Link>
        </div>
      </div>
    </footer>
  );
}
